/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

import { html, css, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { KEYBINDS, getKeybindDisplay, type KeybindConfig } from "./keybinds";
import { hitlKeyboardShortcuts } from "./keyboard-shortcuts";

/**
 * Keyboard layout information for displaying shortcuts correctly per layout
 */
interface KeyboardLayoutInfo {
  code: string;
  name: string;
  // Maps logical key + modifiers to actual key combination for this layout
  keyMappings: {
    [actionKey: string]: {
      [modifiers: string]: string[]; // Array of keys to display
    };
  };
}

const KEYBOARD_LAYOUTS: { [key: string]: KeyboardLayoutInfo } = {
  de: {
    code: "de",
    name: "Deutsch (German)",
    keyMappings: {
      "=": {
        alt: ["Alt", "Shift", "0"], // Alt + = requires Shift + 0 on German keyboards
        "ctrl+shift": ["Strg", "Shift", "0"],
      },
      "-": {
        alt: ["Alt", "ß"], // Alt + - uses the ß key
        ctrl: ["Strg", "ß"],
      },
      "0": {
        alt: ["Alt", "0"],
        "ctrl+alt": ["Strg", "Alt", "0"],
      },
      s: {
        ctrl: ["Strg", "S"],
      },
      c: {
        "ctrl+shift": ["Strg", "Shift", "C"],
      },
      d: {
        "ctrl+shift": ["Strg", "Shift", "D"],
      },
      z: {
        ctrl: ["Strg", "Z"],
        "ctrl+shift": ["Strg", "Shift", "Z"],
      },
    },
  },
  en: {
    code: "en",
    name: "English (US)",
    keyMappings: {
      "=": {
        alt: ["Alt", "="],
        "ctrl+shift": ["Ctrl", "Shift", "="],
      },
      "-": {
        alt: ["Alt", "-"],
        ctrl: ["Ctrl", "-"],
      },
      "0": {
        alt: ["Alt", "0"],
        "ctrl+alt": ["Ctrl", "Alt", "0"],
      },
      s: {
        ctrl: ["Ctrl", "S"],
      },
      c: {
        "ctrl+shift": ["Ctrl", "Shift", "C"],
      },
      d: {
        "ctrl+shift": ["Ctrl", "Shift", "D"],
      },
      z: {
        ctrl: ["Ctrl", "Z"],
        "ctrl+shift": ["Ctrl", "Shift", "Z"],
      },
    },
  },
};

/**
 * Custom keyboard shortcuts dialog that displays layout-aware shortcuts
 * Replaces AIUI's built-in shortcuts dialog which shows incorrect shortcuts for non-US keyboards
 */
@customElement("keyboard-shortcuts-dialog")
export class KeyboardShortcutsDialog extends LitElement {
  @property({ type: Boolean, reflect: true })
  open = false;

  @state()
  private detectedLayout: string = "en";

  static styles = css`
    :host {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: 1000;
      align-items: center;
      justify-content: center;
    }

    :host([open]) {
      display: flex;
    }

    .dialog {
      background: white;
      border-radius: 8px;
      box-shadow:
        0 11px 15px -7px rgba(0, 0, 0, 0.2),
        0 24px 38px 3px rgba(0, 0, 0, 0.14),
        0 9px 46px 8px rgba(0, 0, 0, 0.12);
      max-width: 600px;
      width: 90%;
      max-height: 80vh;
      overflow: auto;
    }

    .header {
      padding: 24px 24px 20px;
      border-bottom: 1px solid #e0e0e0;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .header h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 500;
      color: #333;
    }

    .layout-selector {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #666;
    }

    .layout-selector select {
      padding: 4px 8px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
    }

    .content {
      padding: 24px;
    }

    .section {
      margin-bottom: 24px;
    }

    .section-title {
      font-weight: 500;
      font-size: 14px;
      color: #666;
      text-transform: uppercase;
      margin-bottom: 12px;
      letter-spacing: 0.5px;
    }

    .shortcut-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
      border-bottom: 1px solid #f5f5f5;
    }

    .shortcut-row:last-child {
      border-bottom: none;
    }

    .shortcut-action {
      font-size: 14px;
      color: #333;
    }

    .shortcut-keys {
      display: flex;
      gap: 4px;
      align-items: center;
      flex-shrink: 0;
    }

    .key {
      background: #f5f5f5;
      border: 1px solid #ddd;
      border-radius: 3px;
      padding: 4px 8px;
      font-family: "Courier New", monospace;
      font-size: 12px;
      font-weight: 600;
      color: #333;
      min-width: 24px;
      text-align: center;
    }

    .key-hint {
      font-size: 11px;
      color: #999;
      margin-left: 8px;
      font-style: italic;
    }

    .plus {
      color: #999;
      font-size: 12px;
    }

    .footer {
      padding: 16px 24px;
      border-top: 1px solid #e0e0e0;
      display: flex;
      justify-content: flex-end;
    }

    button {
      background: #be153f;
      color: white;
      border: none;
      border-radius: 4px;
      padding: 8px 16px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      text-transform: uppercase;
    }

    button:hover {
      background: #006b38;
    }

    .info-box {
      background: #e8f5e9;
      border-left: 4px solid #4caf50;
      padding: 12px;
      margin-bottom: 16px;
      font-size: 13px;
      color: #2e7d32;
      border-radius: 4px;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.detectKeyboardLayout();
  }

  /**
   * Detect keyboard layout based on browser language
   */
  private detectKeyboardLayout() {
    const lang = navigator.language.toLowerCase();
    if (lang.startsWith("de")) {
      this.detectedLayout = "de";
    } else {
      this.detectedLayout = "en";
    }
  }

  /**
   * Get layout-specific key combination for a shortcut
   */
  private getLayoutKeys(config: KeybindConfig): string[] {
    const layout = KEYBOARD_LAYOUTS[this.detectedLayout];

    // Build modifier string (e.g., "ctrl+shift", "alt")
    const modifierParts: string[] = [];
    if (config.modifiers.ctrl) modifierParts.push("ctrl");
    if (config.modifiers.alt) modifierParts.push("alt");
    if (config.modifiers.shift) modifierParts.push("shift");
    const modifierKey = modifierParts.join("+");

    // Try to get layout-specific keys
    if (config.key && layout.keyMappings[config.key]?.[modifierKey]) {
      return layout.keyMappings[config.key][modifierKey];
    }

    // Fallback to generic display
    return getKeybindDisplay(config).split("+");
  }

  /**
   * Render a single shortcut row
   */
  private renderShortcut(config: KeybindConfig) {
    const keys = this.getLayoutKeys(config);

    return html`
      <div class="shortcut-row">
        <div class="shortcut-action">${config.description}</div>
        <div class="shortcut-keys">
          ${keys.map(
            (key, index) => html`
              ${index > 0 ? html`<span class="plus">+</span>` : ""}
              <span class="key">${key}</span>
            `,
          )}
        </div>
      </div>
    `;
  }

  /**
   * Get shortcuts for current mode
   */
  private getShortcutsByCategory() {
    const mode = hitlKeyboardShortcuts.getCurrentMode();
    const shortcuts = Object.values(KEYBINDS);

    const always = shortcuts.filter((s) => !s.modes || s.modes.length === 0);
    const modeSpecific = shortcuts.filter(
      (s) => s.modes && s.modes.length > 0 && mode && s.modes.includes(mode),
    );

    return { always, modeSpecific };
  }

  render() {
    const { always, modeSpecific } = this.getShortcutsByCategory();
    const layoutInfo = KEYBOARD_LAYOUTS[this.detectedLayout];

    return html`
      <div class="dialog" @click=${(e: Event) => e.stopPropagation()}>
        <div class="header">
          <h2>Tastaturkürzel / Keyboard Shortcuts</h2>
          <div class="layout-selector">
            <label>Layout:</label>
            <select @change=${this.handleLayoutChange}>
              ${Object.entries(KEYBOARD_LAYOUTS).map(
                ([code, layout]) => html`
                  <option
                    value="${code}"
                    ?selected=${code === this.detectedLayout}
                  >
                    ${layout.name}
                  </option>
                `,
              )}
            </select>
          </div>
        </div>

        <div class="content">
          <div class="section">
            <div class="section-title">Immer verfügbar</div>
            ${always.map((shortcut) => this.renderShortcut(shortcut))}
          </div>

          ${modeSpecific.length > 0
            ? html`
                <div class="section">
                  <div class="section-title">Aktueller Modus</div>
                  ${modeSpecific.map((shortcut) =>
                    this.renderShortcut(shortcut),
                  )}
                </div>
              `
            : ""}
        </div>

        <div class="footer">
          <button @click=${this.close}>Schließen / Close</button>
        </div>
      </div>
    `;
  }

  private handleLayoutChange(e: Event) {
    const select = e.target as HTMLSelectElement;
    this.detectedLayout = select.value;
  }

  show() {
    this.open = true;
  }

  close() {
    this.open = false;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "keyboard-shortcuts-dialog": KeyboardShortcutsDialog;
  }
}
